/* eslint-disable @typescript-eslint/no-explicit-any */
import { watch } from "vue";
import {
  defineComponent,
  ref,
  computed,
  onMounted,
  PropType,
  nextTick,
} from "vue";

interface Option {
  [key: string]: any;
}

export default defineComponent({
  name: "c-select",

  props: {
    value: {
      validator: () => true,
    },
    loading: { type: Boolean, default: false },
    label: { type: String, default: "" },
    error: { type: [Boolean, String], default: false },
    showError: { type: [Boolean, String], default: true },
    style: { type: String, default: "" },
    class: { type: String, default: "" },
    optionValue: { type: String as PropType<string>, default: "value" },
    optionLabel: { type: String as PropType<string>, default: "label" },
    schema: { type: Function as PropType<() => void>, default: () => ({}) },
    options: { type: Array as PropType<Option[]>, default: () => [] },
    searchable: { type: Boolean, default: false },
    placeholder: { type: String, default: "" },
    isTop: { type: Boolean, default: false },
    readonly: { type: Boolean, default: false },
    input: { type: Boolean, default: false },
    clear: { type: Boolean, default: true },
    required: { type: Boolean, default: false },
    filter: { type: String, default: "status" },
    filterValue: { type: [Boolean, Number, String], default: 1 },
    serverEndpoint: {
      type: String,
      default: () => "",
    },
  },

  emits: ["update:value", "enter", "onOptions"],

  setup(props, { emit }) {
    const mockValue = ref(props.value);
    const hasKey = ref(false);
    const className = ref<string>(props.class);
    const styleName = ref<string>(props.style);
    const isRequired = ref<boolean>(false);
    const updatedLabel = ref<string>("");
    const isDropdown = ref<boolean>(false);
    const refIds = "cSelectDropdown";

    const endpoint = "";
    const keySearch = ref("");
    const keyType = "";
    const optionList = ref<Option[]>(getOptions());
    const tempOptions = ref<Option[]>(getOptionsTemp());

    const findOptionLabel = (itemValue: any): string => {
      if (!Array.isArray(props.options)) {
        return "";
      }

      const matchedOption = props.options.find(
        (option: Option) => option[props.optionValue] === itemValue
      );

      if (matchedOption) {
        return (
          matchedOption[props.optionLabel] ||
          String(matchedOption[props.optionValue])
        );
      } else {
        return "";
      }
    };

    function getOptions() {
      return flatValues();
    }

    function getOptionsTemp() {
      return flatValues();
    }

    function flatValues() {
      let option = null;
      let newOptions = [];

      if (props.options.length > 0) {
        hasKey.value = Object.prototype.hasOwnProperty.call(
          props.options[0],
          props.filter
        );
      }

      if (Array.isArray(mockValue.value)) {
        newOptions = mockValue.value.flatMap((value) => {
          const filteredOptions = props.options.filter(
            (opt) => opt[props.optionValue] === value
          );
          return filteredOptions;
        });

        if (hasKey.value) {
          newOptions = newOptions.filter(
            (opt) => opt[props.filter] === props.filterValue
          );
        }
      } else {
        option = props.options.find(
          (item) => item[props.optionValue] === mockValue.value
        );

        if (hasKey.value) {
          newOptions = props.options.filter(
            (opt) => opt[props.filter] === props.filterValue
          );

          if (option?.[props.filter]) {
            newOptions.push(option);
          }
        } else {
          newOptions = props.options;
        }
      }

      return newOptions;
    }

    const selectedValue = ref<string>(findOptionLabel(props.value));

    const isError = computed(() => {
      return props.error && String(props.error); // Coerce to boolean
    });

    watch(
      () => props.value,
      (newValue) => {
        selectedValue.value = findOptionLabel(newValue);
      }
    );

    const listRequired = () => {
      if (props.label && props.label.includes("*")) {
        updatedLabel.value = props.label.replace(/\*/g, "");
        isRequired.value = true;
      } else {
        isRequired.value = false;
        updatedLabel.value = props.label;
      }
    };

    const onFilter = () => {
      if (endpoint && props.searchable) {
        if (keySearch.value && keySearch.value.length > 3) {
          debounce(queriesShorten, 200000);
        } else {
          if (keySearch.value.length === 0) {
            debounce(queriesShorten, 200000);
          }
        }

        // Emit event
        emit(
          "onOptions",
          optionList.value && optionList.value.length
            ? optionList.value
            : tempOptions.value
        );
      }

      if (!props.options) {
        return;
      }

      optionList.value = flatValues().filter((item) => {
        let option = item;

        if (props.optionLabel) {
          option = item[props.optionLabel];
        }

        return option
          .toString()
          .toLowerCase()
          .includes(keySearch.value.toLowerCase());
      });
    };

    const queriesShorten = () => {
      const fullUrl =
        endpoint + (keySearch.value ? `&${[keyType]}=${keySearch.value}` : ``);

      // Call your function for dynamic endpoint
      console.log(fullUrl);
    };

    const debounce = (fn: (...args: any[]) => void, delay: number) => {
      let timeoutId: ReturnType<typeof setTimeout>;
      return (...args: any[]) => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => {
          fn(...args);
        }, delay);
      };
    };

    onMounted(() => {
      listRequired();
    });

    const onSelectValue = (value: any): void => {
      isDropdown.value = false;
      emit("update:value", value?.[props.optionValue]);
      selectedValue.value = findOptionLabel(value?.[props.optionValue]);
      manualInput.value = findOptionLabel(value?.[props.optionValue]);
      props.schema();
    };

    const outsideClick = (): void => {
      isDropdown.value = false;
    };

    const isLoading = computed(() => {
      return !!props.loading;
    });

    const keySearchQuery = ref<HTMLElement | null>(null);

    const onSelect = (): void => {
      if (props.readonly) {
        return;
      }

      isDropdown.value = !isDropdown.value;
      keySearch.value = "";
      onFilter();

      if (props.searchable) {
        nextTick(() => {
          if (isDropdown.value) {
            (keySearchQuery.value as HTMLInputElement).focus();
          }
        });
      }
    };

    const focusIn = (): void => {
      isDropdown.value = true;
    };

    const focusOut = (): void => {
      isDropdown.value = false;
    };

    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key === "Escape") {
        isDropdown.value = false;
      }
    };

    // start copy
    const showPopup = ref(false);

    const copyToClipboard = () => {
      const textArea = document.createElement("textarea");
      let valueToCopy = selectedValue.value;

      if (
        Array.isArray(selectedValue.value) ||
        typeof selectedValue.value === "object"
      ) {
        valueToCopy = JSON.stringify(selectedValue.value);
      }

      textArea.value = String(valueToCopy);
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);

      showPopup.value = true;

      setTimeout(() => {
        showPopup.value = false;
      }, 1500);
    };
    // end copy

    // start manual input
    const manualInput = ref<string>(findOptionLabel(props.value));
    const onManualInput = (): void => {
      emit("update:value", manualInput.value);
      isDropdown.value = true;
    };

    const deleteValue = (): void => {
      emit("update:value", "");
      isDropdown.value = false;
      selectedValue.value = "";
      manualInput.value = "";
      props.schema();
    };

    return {
      refIds,
      className,
      styleName,
      isLoading,
      isError,
      isRequired,
      updatedLabel,
      onSelect,
      isDropdown,
      onSelectValue,
      outsideClick,
      focusIn,
      focusOut,
      handleKeyDown,
      selectedValue,
      optionList,
      onFilter,
      keySearch,
      keySearchQuery,
      copyToClipboard,
      showPopup,
      manualInput,
      onManualInput,
      deleteValue,
    };
  },
});
